import { useEffect, useRef, useState } from "react";
import { PermissionsService } from "./PermissionService";

/**
 * One PermissionsService per object, created once. Constructing the service fetches the
 * permission list and the users and groups, so it must not be built in render: a useMemo or a
 * useState initializer runs twice under React.StrictMode, and a useMemo keyed on a whole state
 * object reruns on every edit. The effect runs twice under StrictMode as well, but the ref
 * survives that and makes the second run reuse the first service.
 *
 * `key` is the object id, or null when the object has no permissions to show.
 */
export function usePermissionsService(key: string | null, create: (key: string) => PermissionsService): PermissionsService | null {
    const created = useRef<{ key: string, service: PermissionsService } | null>(null);
    const [service, setService] = useState<PermissionsService | null>(null);

    useEffect(() => {
        if (!key) {
            created.current = null;
            setService(null);
            return;
        }
        if (created.current?.key !== key) {
            created.current = { key, service: create(key) };
        }
        setService(created.current.service);
        // `create` is a fresh closure every render; the key is what identifies the service
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    // Between a key change and the effect, the state still holds the previous object's service.
    return service && created.current?.key === key ? service : null;
}
